import React, { useRef, useEffect } from 'react';

interface NetworkPulseVisualizationProps {
  height?: number;
}

// Amber/gold palette for DOD + TRON + SPACEFORCE aesthetic
const AMBER = '#C9A227';
const AMBER_GLOW = 'rgba(201, 162, 39, 0.12)';

const NODES = [
  { label: 'Financial Systems' },
  { label: 'Compliance' },
  { label: 'Infrastructure' },
  { label: 'Operations' },
  { label: 'Intelligence' },
  { label: 'Security' },
  { label: 'Data Systems' },
  { label: 'Risk' },
];

const CONNECTIONS = [
  [0, 2], [0, 4], [0, 7],
  [1, 3], [1, 5], [1, 6],
  [2, 4], [2, 6], [2, 7],
  [3, 5], [3, 7], [3, 0],
  [4, 6], [4, 1],
  [5, 7], [5, 0], [5, 2],
  [6, 0], [6, 3],
  [7, 1], [7, 4],
];

const NetworkPulseVisualization: React.FC<NetworkPulseVisualizationProps> = ({ height = 500 }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container) return;

    const render = () => {
      svg.innerHTML = '';

      const width = container.clientWidth;
      const h = container.clientHeight;
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(h));
      svg.setAttribute('viewBox', `0 0 ${width} ${h}`);

      const ns = 'http://www.w3.org/2000/svg';
      const centerX = width / 2;
      const centerY = h / 2;
      const radius = Math.min(width, h) * 0.35;

      // Glow filter
      const defs = document.createElementNS(ns, 'defs');
      const filter = document.createElementNS(ns, 'filter');
      filter.setAttribute('id', 'amber-glow');
      filter.setAttribute('x', '-50%');
      filter.setAttribute('y', '-50%');
      filter.setAttribute('width', '200%');
      filter.setAttribute('height', '200%');
      const blur = document.createElementNS(ns, 'feGaussianBlur');
      blur.setAttribute('stdDeviation', '4');
      blur.setAttribute('result', 'coloredBlur');
      const merge = document.createElementNS(ns, 'feMerge');
      const m1 = document.createElementNS(ns, 'feMergeNode');
      m1.setAttribute('in', 'coloredBlur');
      const m2 = document.createElementNS(ns, 'feMergeNode');
      m2.setAttribute('in', 'SourceGraphic');
      merge.appendChild(m1);
      merge.appendChild(m2);
      filter.appendChild(blur);
      filter.appendChild(merge);
      defs.appendChild(filter);
      svg.appendChild(defs);

      const connectionsGroup = document.createElementNS(ns, 'g');
      const nodesGroup = document.createElementNS(ns, 'g');
      svg.appendChild(connectionsGroup);
      svg.appendChild(nodesGroup);

      // Node positions
      const positions = NODES.map((_, i) => {
        const angle = (i / NODES.length) * Math.PI * 2 - Math.PI / 2;
        return {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle),
        };
      });

      // Connections + particles
      CONNECTIONS.forEach(([fromIdx, toIdx], connIdx) => {
        const from = positions[fromIdx];
        const to = positions[toIdx];

        const midX = centerX + (Math.sin(connIdx * 1.7) * radius * 0.15);
        const midY = centerY + (Math.cos(connIdx * 2.3) * radius * 0.15);
        const pathData = `M ${from.x},${from.y} Q ${midX},${midY} ${to.x},${to.y}`;

        // Connection line
        const path = document.createElementNS(ns, 'path');
        path.setAttribute('d', pathData);
        path.setAttribute('stroke', AMBER);
        path.setAttribute('stroke-width', '0.8');
        path.setAttribute('fill', 'none');
        path.setAttribute('opacity', '0.1');
        connectionsGroup.appendChild(path);

        // Animated particle
        const particle = document.createElementNS(ns, 'circle');
        particle.setAttribute('r', '2');
        particle.setAttribute('fill', AMBER);
        particle.setAttribute('opacity', '0.6');
        particle.setAttribute('filter', 'url(#amber-glow)');

        const animateMotion = document.createElementNS(ns, 'animateMotion');
        const duration = 3 + (connIdx % 5) * 1.2;
        animateMotion.setAttribute('dur', `${duration}s`);
        animateMotion.setAttribute('repeatCount', 'indefinite');
        animateMotion.setAttribute('path', pathData);
        particle.appendChild(animateMotion);
        connectionsGroup.appendChild(particle);
      });

      // Nodes
      NODES.forEach((node, i) => {
        const pos = positions[i];

        // Expanding pulse ring
        const pulseRing = document.createElementNS(ns, 'circle');
        pulseRing.setAttribute('cx', String(pos.x));
        pulseRing.setAttribute('cy', String(pos.y));
        pulseRing.setAttribute('r', '16');
        pulseRing.setAttribute('fill', 'none');
        pulseRing.setAttribute('stroke', AMBER);
        pulseRing.setAttribute('stroke-width', '0.8');
        pulseRing.setAttribute('opacity', '0');

        const animR = document.createElementNS(ns, 'animate');
        animR.setAttribute('attributeName', 'r');
        animR.setAttribute('values', '16;28;38');
        animR.setAttribute('dur', `${2 + (i % 3) * 0.7}s`);
        animR.setAttribute('repeatCount', 'indefinite');
        pulseRing.appendChild(animR);

        const animO = document.createElementNS(ns, 'animate');
        animO.setAttribute('attributeName', 'opacity');
        animO.setAttribute('values', '0.3;0.1;0');
        animO.setAttribute('dur', `${2 + (i % 3) * 0.7}s`);
        animO.setAttribute('repeatCount', 'indefinite');
        pulseRing.appendChild(animO);
        nodesGroup.appendChild(pulseRing);

        // Glow
        const glow = document.createElementNS(ns, 'circle');
        glow.setAttribute('cx', String(pos.x));
        glow.setAttribute('cy', String(pos.y));
        glow.setAttribute('r', '20');
        glow.setAttribute('fill', AMBER_GLOW);
        glow.setAttribute('filter', 'url(#amber-glow)');
        nodesGroup.appendChild(glow);

        // Node circle
        const circle = document.createElementNS(ns, 'circle');
        circle.setAttribute('cx', String(pos.x));
        circle.setAttribute('cy', String(pos.y));
        circle.setAttribute('r', '10');
        circle.setAttribute('fill', '#050508');
        circle.setAttribute('stroke', AMBER);
        circle.setAttribute('stroke-width', '1.5');

        const breathe = document.createElementNS(ns, 'animate');
        breathe.setAttribute('attributeName', 'r');
        breathe.setAttribute('values', '10;11;10');
        breathe.setAttribute('dur', `${3 + (i % 2)}s`);
        breathe.setAttribute('repeatCount', 'indefinite');
        circle.appendChild(breathe);
        nodesGroup.appendChild(circle);

        // Label
        const label = document.createElementNS(ns, 'text');
        label.setAttribute('x', String(pos.x));
        label.setAttribute('y', String(pos.y + 28));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('fill', 'rgba(201, 162, 39, 0.45)');
        label.setAttribute('font-size', '9');
        label.setAttribute('font-family', "'JetBrains Mono', monospace");
        label.setAttribute('font-weight', '500');
        label.setAttribute('letter-spacing', '0.08em');
        label.textContent = node.label.toUpperCase();
        nodesGroup.appendChild(label);
      });
    };

    render();
    window.addEventListener('resize', render);
    return () => window.removeEventListener('resize', render);
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      style={{ height: `${height}px` }}
      role="img"
      aria-label="Network visualization showing interconnected analytical systems with animated data flows"
    >
      <svg ref={svgRef} className="absolute inset-0" />
    </div>
  );
};

export default NetworkPulseVisualization;
